/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import fn_basicsImg from 'assets/images/fn-basics.png'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>4 - Функции</h2>
    <p className={styles.description}>
      Очень часто при создании программ участок кода повторяется. А если программа большая, то возникает необходимость
      повторно использовать подобные участки кода, зачастую с новыми начальными значениями. Воплощением этой идеи
      являются функции.{' '}
    </p>
    <p className={styles.description}>
      <b>Функция</b> - это подпрограмма, независимая частью кода, предназначенная для выполнения конкретной задачи.{' '}
    </p>
    <p className={styles.description}>
      Функции можно представить как черный ящик, они получают что-то на входе (данные), и отдают что-то на выходе
      (результат выполнения кода внутри функции).{' '}
    </p>
    <img className={styles.img} src={fn_basicsImg} alt="fn-basics" />
    <p className={styles.description}>
      Функции это инструмент для структурирования больших программ, уменьшения повторений и изолирования кода.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.1 - Функциональное выражение</h3>
      <p className={styles.description}>
        <b>Функциональное выражение (function expression)</b> — обычное объявление переменной, значение которой будет
        функция.{' '}
      </p>
      <p className={styles.description}>
        Объявим переменную <code className={styles.code}>add</code>, и присвоим ей функцию принимающую 3 значения и
        возвращающую результат сложения этих значений.{' '}
      </p>
      <p className={styles.notificationInfo}>
        Имя функции это действие, глагол начинающийся с маленькой буквы, отвечающий на вопрос <b>'Что сделать?'</b>.
        Например: <code className={styles.code}>findSmallesNumber</code>,{' '}
        <code className={styles.code}>fetchUserInfo</code>, <code className={styles.code}>validateInput</code>.{' '}
      </p>
      <CopyBlock
        text={`const add = function(a, b, c) {
  return a + b + c;
};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Определение функции начинается с ключевого слова <code className={styles.code}>function</code>, за которым может
        следовать необязательное имя функции. В круглых скобках идут <b>параметры</b> — перечисление данных которые
        функция будет получать из вне. Параметров может быть несколько, или вообще их не быть, тогда записываются просто
        пустые круглые скобки <code className={styles.code}>()</code>.{' '}
      </p>
      <p className={styles.description}>
        Далее идет <b>тело функции</b>, заключенное в фигурные скобки <code className={styles.code}>{`{}`}</code>,
        содержащее инструкции которые необходимо выполнить при вызове функции. Тело функции всегда заключают в фигурные
        скобки, даже если оно состоит из одной инструкции.{' '}
      </p>
      <p className={styles.description}>
        Оператор <code className={styles.code}>return</code> определяет возвращаемое значение. Когда интерпретатор
        доходит до <code className={styles.code}>return</code>, он сразу же выходит из функции, и возвращает это
        значение в то место кода, где функция была вызвана.{' '}
      </p>
      <p className={styles.notificationInfo}>
        Оператор <code className={styles.code}>return</code> без выражения возвращает значение{' '}
        <code className={styles.code}>undefined</code>. При отсутствии <code className={styles.code}>return</code> в
        теле функции, она все равно вернет значение <code className={styles.code}>undefined</code>.{' '}
      </p>
      <p className={styles.description}>
        Затем, когда необходимо, функция <b>вызывается</b> с помощью имени и круглых скобок, внутри которых могут быть
        переданы <b>аргументы</b>.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Термин <b>аргументы</b> используется при вызове функции, когда мы передаем значения в функцию.
        </li>
        <li className={styles.list__item}>
          Термин <b>параметры</b> используется при объявлении функции, это те локальные переменные внутри функции, в
          которые будут записаны значения аргументов во время ее вызова.
        </li>
      </ul>
      <p className={styles.description}>
        В JavaScript важен порядок объявления параметров функции. Не существует никакого другого механизма пояснить
        интерпретатору как значения аргументов функции при вызове связаны с параметрами.{' '}
      </p>
      <p className={styles.description}>
        Порядок объявления параметров соответствует порядку передачи аргументов при вызове функции: значение первого
        аргумента будет присвоено первому параметру, второго аргумента второму параметру и т. д. Если параметров будет
        меньше чем аргументов, то параметрам без значений будет присвоено <code className={styles.code}>undefined</code>
        .{' '}
      </p>
      <CopyBlock
        text={`// a, b, c это параметры
const add = function(a, b, c) {
  return a + b + c;
};

// 1, 2, 3 это аргументы
const result = add(1, 2, 3);
console.log(result); // 6

// 5, 10, 15 это аргументы
console.log(add(5, 10, 15)); // 30`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Функция может либо вызвать побочные эффекты (изменять глобальные переменные), либо возвращать результат своей
        работы, зависит от задачи. В зависимости от того, какой фрагмент кода будет помещен внутри функции, произойдет
        либо возврат вычисленного значения, либо изменение состояния программы, иногда оба.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.2 - Порядок выполения кода</h3>
      <p className={styles.description}>
        Если при выполнении кода, интерпретатор встречает вызов функции, он приостанавливает выполнение текущего кода и
        начинает выполнять код из тела функции. После того как весь код функции будет выполнен, интерпретатор выходит из
        тела функции, возвращаясь в то место, откуда пришел, и продолжает выполнять код ниже вызова функции.
      </p>
      <CopyBlock
        text={`const fnA = function() {
  console.log('Начала выполняться [fnA]');
  fnB();
  console.log('Продолжила выполняться [fnA] после выхода из [fnB]');
};

const fnB = function() {
  console.log('Выполняется [fnB]');
};

console.log('Начал выполнение [main]');
fnA();
console.log('Продолжил выполняться [main] после выхода из [fnA]');

/*
 * Начал выполнение [main]
 * Начала выполняться [fnA]
 * Выполняется [fnB]
 * Продолжила выполняться [fnA] после выхода из [fnB]
 * Продолжил выполняться [main] после выхода из [fnA]
 */`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Пошагово разберем выполнение кода примера: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>При входе в js-файл, код начинает выполняться сверху вниз.</li>
        <li className={styles.list__item}>
          Вызов <code className={styles.code}>fnA()</code> заставляет интерпретатор приостановить исполнение кода, и
          зайти в тело функции <code className={styles.code}>fnA</code>.
        </li>
        <li className={styles.list__item}>
          В функции <code className={styles.code}>fnA</code> интерпретатор встречает вызов функции{' '}
          <code className={styles.code}>fnB</code>, которая перехватывает контроль и исполняется.
        </li>
        <li className={styles.list__item}>
          Как только интерпретатор выполнил функцию <code className={styles.code}>fnB</code>, тоесть в ней произошел{' '}
          <code className={styles.code}>return</code>, в нашем случае не явный, интерпретатор возвращается в функцию{' '}
          <code className={styles.code}>fnA</code>и продолжает выполнять ее с того места где остановился.
        </li>
        <li className={styles.list__item}>
          Как только интерпретатор выполнил функцию <code className={styles.code}>fnA</code>, тоесть в ней произошел{' '}
          <code className={styles.code}>return</code>, в нашем случае не явный, интерпретатор возвращается в глобальный
          код и продолжает его выполнение.
        </li>
      </ul>
      <p className={styles.notificationInfo}>
        Вызовы <code className={styles.code}>console.log()</code>, так как это функция, тоже перехватывают контроль. Они
        были пропущены для более понятного описания процесса.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.3 - Параметры по умолчанию</h3>
      <p className={styles.description}>
        При вызове функции необязательно указывать значения всех аргументов, которые перечислены в параметрах функции.
        Но иногда, параметрам которым не передали значение через аргумент, бывает необходимо присвоить какое-то
        значение, отличное от <code className={styles.code}>undefined</code>.{' '}
      </p>
      <p className={styles.description}>
        Новые возможности ES6 позволяют сделать это очень простым и очевидным образом, указывая значение по умолчанию
        прямо при объявлении параметров в подписи функции. При такой записи, если значение переданного аргумента равно{' '}
        <code className={styles.code}>undefined</code>, в параметр записывается значение по умолчанию.{' '}
      </p>
      <CopyBlock
        text={`const count = function(countFrom = 0, countTo = 10, step = 1) {
  console.log(\`countFrom = $ {countFrom}, countTo = $ {countTo}, step = $ {step}\`);

  for (let i = countFrom; i <= countTo; i += step) {
    console.log(i);
  }
};

count(1, 5); // countFrom = 1, countTo = 5, step = 1
count(2); // countFrom = 2, countTo = 10, step = 1
count(undefined, 5, 2); // countFrom = 0, countTo = 5, step = 2
count(); // countFrom = 0, countTo = 10, step = 1`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.4 - Псевдомассив arguments</h3>
      <p className={styles.description}>
        Доступ к набору всех аргументов можно получить через специальную локальную переменную{' '}
        <code className={styles.code}>arguments</code>, которая доступна только внутри функции и хранит все аргументы
        как псевдомассив.{' '}
      </p>
      <p className={styles.description}>
        <b>Псевдомассив</b> — коллекция, имеющая свойство <code className={styles.code}>length</code> и возможность
        обратиться к элементу по индексу, но отсутствием большинства методов для работы с массивом.{' '}
      </p>
      <p className={styles.description}>
        Псевдомассив <code className={styles.code}>arguments</code> содержит список <b>аргументов</b>, переданных
        функции при вызове, но количество переданных аргументов может не совпадать с количеством объявленных параметров
        и это не приведет к ошибке.{' '}
      </p>
      <p className={styles.description}>
        Рассмотрим пример использования <code className={styles.code}>arguments</code> в функции, которая суммирует
        любое количество аргументов:{' '}
      </p>
      <CopyBlock
        text={`const sum = function() {
  let total = 0;

  for (const argument of arguments) {
    total += argument;
  }

  return total;
};

console.log(sum(1, 2, 3)); //  6
console.log(sum(1, 2, 3, 4)); //  10
console.log(sum(1, 2, 3, 4, 5)); //  15`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.4.1 - Способы преобразовать псевдомассив</h4>
        <p className={styles.description}>
          Так как у псевдомассива нету методов вроде <code className={styles.code}>slice()</code> или{' '}
          <code className={styles.code}>includes()</code>, часто необходимо преобразовать его в полноценный массив. На
          практике используют несколько основных способов преобразования.{' '}
        </p>
        <p className={styles.description}>
          Используя метод <code className={styles.code}>Array.from()</code>, который создаст массив из итерируемого
          объекта.{' '}
        </p>
        <CopyBlock
          text={`const fn = function() {
  // В переменной args будет полноценный массив
  const args = Array.from(arguments);
};`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Используя операцию <code className={styles.code}>...</code> (rest), она позволяет собрать произвольное
          количество элементов, в нашем случае аргументов, в массив и сохранить его в переменную. Собираем все аргументы
          используя операцию <code className={styles.code}>rest</code> прямо в подписи функции.
        </p>
        <p className={styles.notificationInfo}>
          Операция <code className={styles.code}>rest</code> более детально рассматривается дальше в курсе, сдесь же
          показано одно из ее возможных применений.{' '}
        </p>
        <CopyBlock
          text={`const fn = function(...args) {
  // В переменной args будет полноценный массив
};`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.5 - Паттерн Guard Clause</h3>
      <p className={styles.description}>
        Оператор <code className={styles.code}>if...else</code> это основной способ создания логических деревьев. Тем не
        менее, сложные вложенные ветвления делают код более запутанным и трудным для понимания.{' '}
      </p>
      <p className={styles.description}>
        Создадим функцию которая обрабатывает снятие денег с личного счета в банке. Функция получает сумму для снятия и
        текущий баланс счета, после чего в зависимости от условия, выполняет тот или иной блок кода.{' '}
      </p>
      <CopyBlock
        text={`const withdraw = function(amount, balance) {
  if (amount === 0) {
    console.log('Для проведения операции введите сумму больше нуля.');
  } else if (amount > balance) {
    console.log('Недостаточно средств на счету.');
  } else {
    console.log('Операция снятия средств проведена.');
  }
};

withdraw(0, 300); // Для проведения операции введите сумму больше нуля.
withdraw(500, 300); // Недостаточно средств на счету.
withdraw(100, 300); // Операция снятия средств проведена.`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Даже в таком простом примере есть группа вложенных условных операторов, среди которых не сразу можно выделить
        нормальный ход выполнения кода из-за особенности синтксиса <code className={styles.code}>if...else</code>.{' '}
      </p>
      <p className={styles.description}>
        <b>Паттерн guard clause</b> - это способ использовать возможность досрочного возврата из функции с помощью
        оператора <code className={styles.code}>return</code>, чтобы сделать вложенные условные выражения более
        одномерными.{' '}
      </p>
      <p className={styles.description}>
        Выделим все проверки условий в отдельные операторы <code className={styles.code}>if</code>, после чего добавим
        код идущий в теле <code className={styles.code}>else</code>. В идеальном случае, должен получиться плоский
        список условных операторов, идущих один за другим, а в конце блок который выполнится только в том случае если не
        выполнится ни один <code className={styles.code}>if</code>.{' '}
      </p>
      <CopyBlock
        text={`const withdraw = function(amount, balance) {
  /*
   * Проверяется условие. Если оно выполняется, происходит
   * console.log и выход из функции. Код идущий после тела if
   * не выполнится.
   */
  if (amount === 0) {
    console.log('Для проведения операции введите сумму больше нуля.');
    return;
  }

  /*
   * Если условие первого if не выполнилось, его тело пропускается
   * и интерпретатор доходит до этого if.
   * Проверяется условие. Если оно выполняется, происходит
   * console.log и выход из функции. Код идущий после тела if
   * не выполнится.
   */
  if (amount > balance) {
    console.log('Недостаточно средств на счету.');
    return;
  }

  /*
   * Если ни один из предыдущих if не выполнился,
   * интерпретатор доходит до этого кода и выполняет его.
   */
  console.log('Операция снятия средств проведена.');
};

withdraw(0, 300); // Для проведения операции введите сумму больше нуля.
withdraw(500, 300); // Недостаточно средств на счету.
withdraw(100, 300); // Операция снятия средств проведена.`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Используя такой подход получаем более чистый, плоский и понятный код, который легче рефакторить.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.6 - Объявление функции</h3>
      <p className={styles.description}>
        Другой способ создать функцию — использовать ключевое слово <code className={styles.code}>function</code> в
        начале инструкции. Такая запись называется <b>объявление функции (function declaration)</b>.{' '}
      </p>
      <p className={styles.description}>
        Инструкция определяет переменную <code className={styles.code}>add</code> и присваивает ей заданную функцию. Эта
        форма записи не требует обязательной точки с запятой после закрытия фигурной скобки тела функции.{' '}
      </p>
      <CopyBlock
        text={`function add(a, b, c) {
  return a + b + c;
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Особенностью этого синтаксиса является то, что функцию можно вызвать до ее объявления в коде. Это может быть
        удобно, потому что не нужно беспокоиться о необходимости определять все функции выше того места, где они
        используются. Механизм поднятия идентификаторов описан{' '}
        <a className={styles.link} href="scope.html#hoisting">
          тут
        </a>
        .{' '}
      </p>
      <CopyBlock
        text={`add(1, 2, 3); // 6
print('text'); // text

function add(a, b, c) {
  return a + b + c;
}

function print(str) {
  console.log(str);
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        С другой стороны, при таком синтаксисе может быть сложно отследить связь между местом объявления функции и
        местом ее вызова. Современные гайды по стилю кода, составленные большими компаниями с многолетним опытом
        разработки, так же склоняется к использованию функциональных выражений. Поэтому рекомендуется использовать
        функциональные выражения, вместо объявлений функции.{' '}
      </p>
      <CopyBlock
        text={`// ❌ Плохо
function fn() {}

// ✅ Хорошо
const fn = function() {};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
